import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { HeartIcon } from 'lucide-react';
import { Fade, Hi, Slide, SlideHeader } from '../deck/Slide';
import { FloatingHearts } from '../deck/FloatingHearts';
const brands = ['Adtralza', 'Enstilar', 'Anzupgo', 'Protopic', 'Fusidin', 'Travo'];
const campaigns = ['DermX', 'TraloSpeak', 'CHE Patients', 'Anzupgo launch', 'Enstilar refresh'];
const countries = ['Spain', 'LATAM', 'UK', 'UAE', 'Australia', 'Poland', 'Germany', 'France', 'Italy', 'Canada', 'Netherlands'];
type MarqueeProps = {
  items: string[];
  direction?: 'left' | 'right';
  duration?: number;
  variant: 'brand' | 'campaign' | 'country';
};
function Marquee({
  items,
  direction = 'left',
  duration = 34,
  variant
}: MarqueeProps) {
  const reduceMotion = useReducedMotion();
  const track = [...items, ...items];
  const tileClass = variant === 'brand' ? 'rounded-2xl bg-[var(--brand-green)] px-[clamp(0.8rem,1.6vw,1.6rem)] py-[clamp(0.45rem,0.9vw,0.9rem)] text-[clamp(1.05rem,2vw,2rem)] font-bold text-white' : variant === 'campaign' ? 'rounded-2xl bg-[var(--brand-green-light)] px-[clamp(0.8rem,1.5vw,1.5rem)] py-[clamp(0.4rem,0.8vw,0.8rem)] text-[clamp(0.95rem,1.7vw,1.7rem)] font-bold text-white' : 'rounded-full border border-black/10 bg-[var(--surface)] px-[clamp(0.75rem,1.4vw,1.4rem)] py-[clamp(0.35rem,0.7vw,0.7rem)] text-[clamp(0.9rem,1.5vw,1.5rem)] font-semibold text-[var(--muted)]';
  return <div className="relative flex min-h-0 flex-1 items-center overflow-hidden" data-id="element-261" data-mp="653udyhia5nx">
      <motion.div className="flex w-max items-center gap-[clamp(0.5rem,1vw,1rem)] pr-[clamp(0.5rem,1vw,1rem)]" animate={reduceMotion ? undefined : {
      x: direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%']
    }} transition={reduceMotion ? undefined : {
      duration,
      ease: 'linear',
      repeat: Infinity
    }} data-id="element-262" data-mp="677tm34gmghv">
        {track.map((item, index) => <React.Fragment key={`${item}-${index}`} data-id="element-263" data-mp="ys3isxtibncu" data-mpi={index}>
            <span className={`flex flex-shrink-0 items-center leading-none ${tileClass}`} data-id="element-264" data-mp="ypr20nzhjj1d">
              {item}
            </span>
            <HeartIcon className={`h-[clamp(0.7rem,1.1vw,1.1rem)] w-[clamp(0.7rem,1.1vw,1.1rem)] flex-shrink-0 ${variant === 'country' ? 'text-[var(--brand-green-lighter)]' : 'text-[var(--brand-green-light)]'}`} fill="currentColor" aria-hidden="true" data-id="element-265" data-mp="pqwzkgzh3yfw" />
          </React.Fragment>)}
      </motion.div>
    </div>;
}
export function TractionSlide() {
  return <Slide data-id="element-266" data-mp="21kv1uf3cejx">
      <FloatingHearts colorClassName="text-[var(--brand-green-light)]" data-id="element-267" data-mp="224bt7t08wwd" />

      <div className="relative z-10 flex min-h-0 flex-1 flex-col" data-id="element-268" data-mp="1zrki41cxa6n">
        <SlideHeader eyebrow="Where we already stand" data-id="element-269" data-mp="3t7chaipqz71g">
          Brand after brand, market after market,{' '}
          <Hi data-id="element-270" data-mp="3o9kmngxpx6tk">the love keeps coming back.</Hi>
        </SlideHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-[clamp(0.4rem,0.9vw,0.9rem)]" data-id="element-271" data-mp="23pp1xerhqxki">
          <Marquee items={brands} variant="brand" duration={36} data-id="element-272" data-mp="28p1wymsinay6" />
          <Marquee items={campaigns} variant="campaign" direction="right" duration={40} data-id="element-273" data-mp="2doy24pcy98fb" />
          <Marquee items={countries} variant="country" duration={46} data-id="element-274" data-mp="2dn3hlc5lbibr" />
        </div>

        <Fade delay={0.4} className="mt-[clamp(0.8rem,1.5vw,1.5rem)] flex-shrink-0" data-id="element-275" data-mp="2hof95n80ydml">
          <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 border-t border-black/10 pt-[clamp(0.7rem,1.3vw,1.3rem)]" data-id="element-276" data-mp="1l1tu0bik8dzb">
            <p className="flex items-center gap-3 text-[clamp(1rem,1.5vw,1.5rem)] font-bold text-[var(--fg)]" data-id="element-277" data-mp="1u173h46op2k1">
              <HeartIcon className="h-5 w-5 text-[var(--brand-green-light)]" fill="currentColor" aria-hidden="true" data-id="element-278" data-mp="1ys6wvzglh2hs" />
              Loved by brand, country and channel teams alike.
            </p>
            <p className="max-w-[44ch] text-[clamp(0.85rem,1.05vw,1.05rem)] leading-snug text-[var(--muted)]" data-id="element-279" data-mp="1699vjo6cuuo7">
              The missing link is bringing it all under one central viewpane, so
              every market inherits the work instead of repeating it.
            </p>
          </div>
        </Fade>
      </div>
    </Slide>;
}